import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { api } from '@/api/clients'
import { qk } from '@/api/queryKeys'
import { useSelfId } from '@/auth/session'
import { Badge } from '@/components/Badge'
import { Countdown } from '@/components/Countdown'
import { CurrencyReadout } from '@/components/CurrencyReadout'
import { Icon } from '@/components/Icon'
import { Meter } from '@/components/Meter'
import { healthTone } from '@/components/tones'
import { Spinner } from '@/components/Spinner'
import type { BuildingInfo } from '@/gen/building/v1/building_pb'
import { formatAgo, toMs } from '@/lib/format'
import { useNow } from '@/lib/useNow'
import { factionToken } from '@/map/geojson'
import { selectionHref } from '@/map/selection'
import { BUILDING_MAX_HEALTH } from '@/panels/BuildingPanel'
import { buildingStatusKey, buildingStatusTone, humanize } from '@/panels/labels'
import { PanelError } from '@/panels/PanelState'
import { sortBuildings, type PropertySortKey, type SortDir } from './sortBuildings'

const COLUMNS: { key: PropertySortKey; align?: 'right' }[] = [
  { key: 'name' },
  { key: 'type' },
  { key: 'tier', align: 'right' },
  { key: 'status' },
  { key: 'income', align: 'right' },
  { key: 'collect' },
  { key: 'health' },
]

function CollectCell({ b, now }: { b: BuildingInfo; now: number }) {
  const { t } = useTranslation()
  if (b.collectReadyAtUnixMs === undefined) return <span className="text-ink-fade">—</span>
  const at = toMs(b.collectReadyAtUnixMs)
  if (at <= now) return <Badge tone="success">{t('building.collectReady')}</Badge>
  return <Countdown target={at} done={t('building.collectReady')} className="text-[13px] text-ink-soft" />
}

export interface PropertyTableProps {
  buildings: readonly BuildingInfo[]
  selfId: string | null
  onOpen: (buildingId: string) => void
}

/**
 * The sortable holdings table. Every row here is the viewer's own, but the
 * last-visited column still checks ownership row by row: that timestamp is
 * stamped only by in-person actions and must never render for anyone else.
 */
export function PropertyTable({ buildings, selfId, onOpen }: PropertyTableProps) {
  const { t } = useTranslation('common')
  const now = useNow(1000)
  const [sort, setSort] = useState<{ key: PropertySortKey; dir: SortDir }>({ key: 'name', dir: 'asc' })
  const rows = useMemo(() => sortBuildings(buildings, sort.key, sort.dir), [buildings, sort])

  function onSort(key: PropertySortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' || key === 'type' ? 'asc' : 'desc' }))
  }

  return (
    <div className="soc-card overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse text-[14px]" data-testid="property-table">
        <thead>
          <tr className="border-b border-border-light bg-bg-deep">
            {COLUMNS.map((c) => {
              const active = sort.key === c.key
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={`p-0 ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  <button
                    type="button"
                    onClick={() => onSort(c.key)}
                    className={`soc-label inline-flex w-full items-center gap-1.5 px-4 py-3 hover:text-ink ${
                      c.align === 'right' ? 'justify-end' : ''
                    } ${active ? 'text-ink' : ''}`}
                    data-testid={`sort-${c.key}`}
                  >
                    {t(`property.col.${c.key}`)}
                    <span aria-hidden className={active ? 'text-brass' : 'text-ink-fade'}>
                      {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
              )
            })}
            <th scope="col" className="soc-label px-4 py-3 text-left font-normal">
              {t('property.col.visited')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => {
            const mine = Boolean(selfId) && b.ownerPlayerId === selfId
            const visited = mine && b.lastVisitedAtUnixMs !== undefined ? toMs(b.lastVisitedAtUnixMs) : 0
            return (
              <tr
                key={b.id}
                onClick={() => onOpen(b.id)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(b.id)}
                tabIndex={0}
                className="cursor-pointer border-b border-border transition-colors last:border-b-0 hover:bg-panel-light focus-visible:bg-panel-light"
                data-testid="property-row"
                data-building-id={b.id}
              >
                <td className="relative px-4 py-3">
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-[3px]"
                    style={{ background: `var(--soc-${factionToken(b.ownerFaction)})` }}
                  />
                  <span className="soc-proper text-[16px] text-ink">{b.name}</span>
                </td>
                <td className="px-4 py-3 text-ink-soft">{humanize(b.gameType) || '—'}</td>
                <td className="soc-num px-4 py-3 text-right text-ink">T{b.tier}</td>
                <td className="px-4 py-3">
                  <Badge tone={buildingStatusTone(b.status)}>{t(`building.status.${buildingStatusKey(b.status)}`)}</Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  {toMs(b.tierIncome) > 0 ? <CurrencyReadout currency="dirty" amount={b.tierIncome} size="sm" /> : <span className="text-ink-fade">—</span>}
                </td>
                <td className="px-4 py-3">
                  <CollectCell b={b} now={now} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex w-[110px] items-center gap-2">
                    <Meter value={b.health} max={BUILDING_MAX_HEALTH} tone={healthTone(b.health, BUILDING_MAX_HEALTH)} className="flex-1" />
                    <span className="soc-num w-7 text-right text-[12px] text-ink-soft">{b.health}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[13px] text-ink-mute" data-testid="visited-cell">
                  {visited > 0 ? formatAgo(now - visited) : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function PropertyPage() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const selfId = useSelfId()
  const now = useNow(30_000)
  const q = useQuery({
    queryKey: qk.playerBuildings(selfId ?? 'none'),
    queryFn: ({ signal }) => api().building.listPlayerBuildings({ owner: { case: 'playerId', value: selfId! } }, { signal }),
    enabled: Boolean(selfId),
    refetchInterval: 60_000,
  })
  // The catch-up share an uncollected holding pays (0 = "nothing to report").
  const config = useQuery({
    queryKey: ['config', 'building'],
    queryFn: ({ signal }) => api().building.getPublicConfig({}, { signal }),
    staleTime: 10 * 60_000,
    retry: false,
  })
  const buildings = q.data?.buildings ?? []
  const totalIncome = buildings.reduce((sum, b) => sum + toMs(b.tierIncome), 0)
  const ready = buildings.filter((b) => b.collectReadyAtUnixMs !== undefined && toMs(b.collectReadyAtUnixMs) <= now).length
  const catchup = config.data?.collectCatchupPct ?? 0

  return (
    <div className="soc-backdrop h-full overflow-y-auto" data-testid="property-page">
      <div className="mx-auto max-w-[1180px] px-6 py-8 lg:px-10">
        <p className="soc-label text-brass">{t('nav.empire')}</p>
        <h1 className="soc-title mt-1 text-[32px]">{t('property.title')}</h1>
        <p className="mt-2 max-w-[70ch] text-ink-soft">{t('property.subtitle')}</p>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="soc-card px-4 py-3">
            <div className="soc-label">{t('property.holdings')}</div>
            <div className="soc-num mt-1 text-[24px] text-ink">{buildings.length}</div>
          </div>
          <div className="soc-card px-4 py-3">
            <div className="soc-label">{t('property.dailyIncome')}</div>
            <div className="mt-1">
              <CurrencyReadout currency="dirty" amount={totalIncome} size="lg" />
            </div>
            {catchup > 0 && catchup < 100 && totalIncome > 0 && (
              <div className="mt-1 text-[12px] text-ink-mute">
                {t('property.catchup', { pct: catchup })}{' '}
                <CurrencyReadout currency="dirty" amount={Math.floor((totalIncome * catchup) / 100)} size="sm" />
              </div>
            )}
          </div>
          <div className="soc-card px-4 py-3">
            <div className="soc-label">{t('property.readyToCollect')}</div>
            <div className={`soc-num mt-1 text-[24px] ${ready > 0 ? 'text-success' : 'text-ink'}`}>{ready}</div>
          </div>
        </div>

        <div className="mt-6">
          {q.isPending ? (
            <Spinner label={t('panel.loading')} />
          ) : q.isError ? (
            <PanelError error={q.error} />
          ) : buildings.length === 0 ? (
            <div className="soc-card p-8 text-center">
              <p className="soc-flavour text-[18px]">{t('property.emptyFlavour')}</p>
              <p className="mt-2 text-ink-mute">{t('property.emptyBody')}</p>
            </div>
          ) : (
            <PropertyTable buildings={buildings} selfId={selfId} onOpen={(id) => navigate(selectionHref({ kind: 'building', id }))} />
          )}
        </div>
        <p className="mt-4 flex items-center gap-2 text-[13px] text-ink-mute">
          <Icon name="arrowRight" size={14} /> {t('property.rowHint')}
        </p>
      </div>
    </div>
  )
}
